import React from 'react';
import { useTranslation } from '@shared/core';
import { ChevronLeftSquare, Help, WarningTriangle } from '@withjoy/joykit/icons';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { CheckIcon } from '@assets/icons';
import { Box, Flex, TextV2, TooltipV2, useDisclosure, useToast } from '@withjoy/joykit';
import { useEventInfo } from '@shared/utils/eventInfo';
import { HotelAddEditProps } from './HotelForm.types';
import { useNavigationPaneContext } from '@website-editor/context/NavigationPaneContext/NavigationPane.context';
import { useCustomComponentStateContext } from '@website-editor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';
import { useAuth } from '@shared/components/AuthProvider';
import { CUSTOM_PAGE_ACTIONS } from '@website-editor/customComponents/CustomComponents.constants';
import { useAdminAccommodationsTelemetry } from '../../Accommodations.telemetry';
import { TravelMapBlockType } from '../../Accommodations.types';
import { useUpdateWebsitePreview } from '@website-editor/hooks/useUpdateWebsitePreview';
import { AccommodationPlaceV2 } from '../CustomProperty/CustomPropertyForm.types';
import { useAlignAccommodationPlaceData } from '../../hooks/useAlignAccommodationPlaceData';
import { ScrollableArea } from '@website-editor/utils/WebsiteEditor.utils';
import { pxToRem } from '@withjoy/joykit/theme';
import { useCancelRoomBlock } from '../../hooks/useCancelRoomBlock';
import { AccommodationsDeleteDialog } from '../../components/AccommodationsDeleteDialog';
import { RoomBlockCancelDialog } from '../../components/RoomBlockCancelDialog';
import { useManageAccommodationPlace } from '../../hooks/useManageAccommodationPlace';
import { HotelForm } from '../../components/HotelForm';
import { formatNowForTelemetry } from '@shared/utils/telemetryDateFormatter';
import { HotelPreview } from '../../components/HotelForm/HotelForm.HotelPreview';

export const HotelAddEdit: React.FC<HotelAddEditProps> = props => {
  const { pageSlug, isEditMode = false, containerId } = props;

  const { toast } = useToast();
  const { eventHandle } = useEventInfo();
  const { t } = useTranslation('websiteEditor');
  const telemetry = useAdminAccommodationsTelemetry();
  const { updateWebsitePreview } = useUpdateWebsitePreview();
  const { navigateBack, replaceAndNavigateToScreen, isAnimating } = useNavigationPaneContext();
  const { isOpen: isOpenRoomBlockCancelDialog, onClose: onCloseRoomBlockCancelDialog, onOpen: onOpenRoomBlockCancelDialog } = useDisclosure();
  const {
    customComponentBlockData: hotelDataProps,
    setCustomComponentBlockData,
    setMatchedHotel,
    setRefetchQueries,
    matchedHotel,
    setShowDeletePopup,
    setSelectedHotelPhotos,
  } = useCustomComponentStateContext<AccommodationPlaceV2>();
  const hotelData = useAlignAccommodationPlaceData(hotelDataProps);

  const travelMapAccommodationsHotelTrans = t('customPage', 'itemTypes', 'travelMapAccommodationsHotel');
  const cancelRoomBlock = t('customPage', 'cancelRoomBlock');

  const { isSuperAdmin } = useAuth();

  const title = isEditMode ? travelMapAccommodationsHotelTrans.title.editHotel() : travelMapAccommodationsHotelTrans.title.add();

  const { saveAccommodationPlace, savingAccommodationPlace } = useManageAccommodationPlace({
    isEditMode,
    id: hotelData?.id,
    onSuccess: () => {
      updateWebsitePreview();
      setRefetchQueries(['AccommodationHotels']);
      setCustomComponentBlockData({
        __typename: 'AccommodationPlaceV2',
        isFromAccommodationCatalog: false,
      });
      replaceAndNavigateToScreen(CUSTOM_PAGE_ACTIONS.CUSTOM_COMPONENTS);
      setMatchedHotel(null);
      setSelectedHotelPhotos(null);
    },
  });

  const onCancelError = useEventCallback(() => toast(cancelRoomBlock.submitError(), { icon: <WarningTriangle /> }));

  const handleOnCancelRoomBlock = useEventCallback(() => {
    updateWebsitePreview();
    setCustomComponentBlockData({
      __typename: 'AccommodationPlaceV2',
      isFromAccommodationCatalog: false,
    });
    setRefetchQueries(['AccommodationHotels']);
    replaceAndNavigateToScreen(CUSTOM_PAGE_ACTIONS.CUSTOM_COMPONENTS);
    setMatchedHotel(null);
    setSelectedHotelPhotos(null);
  });

  const onCancelled = useEventCallback(() => {
    handleOnCancelRoomBlock?.();
    toast(cancelRoomBlock.successMessage(), { icon: <CheckIcon /> });
    onCloseRoomBlockCancelDialog();
  });

  const { canceling, onCancelRoomBlock } = useCancelRoomBlock({
    pageSlug,
    eventHandle: eventHandle || '',
    isSuperAdmin: true,
    displayName: (hotelData?.customName || hotelData?.name) ?? '',
    onCancelled,
    onCancelError,
  });

  const hotelDataWithMatchedHotel =
    matchedHotel?.zhId || matchedHotel?.joyPlaceId
      ? {
          __typename: 'AccommodationPlaceV2' as const,
          joyPlaceId: matchedHotel.joyPlaceId,
          zhId: matchedHotel.zhId || '',
          name: matchedHotel.displayName || '',
          city: matchedHotel.city || '',
          state: matchedHotel.state || '',
          googlePlaceId: '',
          country: matchedHotel.country || '',
          postalCode: matchedHotel.postalCode || '',
          starRating: matchedHotel.starRating,
          address: matchedHotel.address || '',
          photos: matchedHotel.photos,
          distance: matchedHotel.distance,
          photo: { __typename: 'Photo' as const, id: matchedHotel.photo || '', url: matchedHotel.photo || '' },
        }
      : (hotelData ?? { __typename: 'AccommodationPlaceV2', isFromAccommodationCatalog: false });

  const handleCancelAdd = useEventCallback(() => {
    if (isEditMode) {
      telemetry.editItemCancel(pageSlug, { itemType: TravelMapBlockType.travelMapAccommodationsHotel, isSuperAdmin });
    } else {
      telemetry.addItemCancel(pageSlug, { itemType: TravelMapBlockType.travelMapAccommodationsHotel, isSuperAdmin });
    }
    setCustomComponentBlockData({
      __typename: 'AccommodationPlaceV2',
      isFromAccommodationCatalog: false,
    });
    setMatchedHotel(null);
    setSelectedHotelPhotos(null);
    navigateBack();
  });

  const handleDelete = useEventCallback(() => {
    setShowDeletePopup(true);
  });

  // Cancel Room Block
  const handleCancelRoomBlock = useEventCallback(() => {
    const currentDateTime = formatNowForTelemetry();
    telemetry.cancelRoomBlock(pageSlug, {
      roomBlockId: hotelData?.id ?? '',
      isSuperAdmin: true,
      website: eventHandle || '',
      datetime: currentDateTime,
    });
    onOpenRoomBlockCancelDialog();
  }, [pageSlug, hotelData?.id, eventHandle, telemetry, onOpenRoomBlockCancelDialog]);

  return (
    <ScrollableArea
      backgroundColor="white"
      flexDirection="column"
      maxHeight="100%"
      width="100%"
      overflowY="auto"
      alignItems="center"
      borderRight={['0px solid', '1px solid']}
      borderColor={['mono3', 'mono3']}
      transparentScrollbar={isAnimating}
    >
      <Box
        alignItems="center"
        width="100%"
        height="100%"
        paddingX={{ _: 7, sm2: 8 }}
        paddingTop={7}
        position="relative"
      >
        <Flex
          borderRadius={pxToRem(48)}
          border="1px solid"
          borderColor="mono3"
          background="white"
          width={8}
          height={8}
          position="absolute"
          padding={2}
          justifyContent="center"
          alignItems="center"
          gap={3}
          left={pxToRem(20)}
          cursor="pointer"
          top={pxToRem(20)}
          zIndex={999}
          onClick={handleCancelAdd}
          _hover={{
            backgroundColor: 'mono3',
            borderColor: 'mono5',
          }}
          _active={{
            backgroundColor: 'mono5',
            borderColor: 'mono5',
          }}
        >
          <ChevronLeftSquare size={16} />
        </Flex>
        <TooltipV2
          overrides={{
            Content: { props: { paddingY: 4, paddingLeft: pxToRem(14), paddingRight: 4, borderRadius: pxToRem(12), color: 'white' } },
          }}
          content={
            <Flex
              flexDirection="column"
              maxWidth={pxToRem(256)}
            >
              <Box typographyVariant="label3">{travelMapAccommodationsHotelTrans.editHotelTooltip.title()}</Box>
              <Box
                typographyVariant="label2"
                color="mono4"
              >
                {travelMapAccommodationsHotelTrans.editHotelTooltip.content()}
              </Box>
            </Flex>
          }
          onMouseEnterDelay={500}
        >
          <Box
            position="absolute"
            top={pxToRem(20)}
            right={pxToRem(20)}
            zIndex={999}
            paddingTop={3}
            display={{ _: 'none', sm2: 'flex' }}
          >
            <Help
              size={24}
              color="mono10"
            />
          </Box>
        </TooltipV2>
        <TextV2
          typographyVariant="hed5"
          color="mono14"
          marginY={7}
          textAlign="center"
        >
          {title}
        </TextV2>
        <HotelPreview hotelData={hotelDataWithMatchedHotel} />
        <HotelForm
          // Reset form state when entity changes (React recommended key pattern)
          key={matchedHotel?.zhId || matchedHotel?.joyPlaceId || hotelData?.id || 'new'}
          isEditMode={isEditMode}
          isLoading={savingAccommodationPlace}
          hotelData={hotelDataWithMatchedHotel}
          handleCancel={handleCancelAdd}
          handleSubmit={saveAccommodationPlace}
          handleDelete={handleDelete}
          containerId={containerId}
          handleCancelRoomBlock={handleCancelRoomBlock}
        />
      </Box>
      {hotelData?.id && (
        <RoomBlockCancelDialog
          loading={canceling}
          isOpen={isOpenRoomBlockCancelDialog}
          onClose={onCloseRoomBlockCancelDialog}
          onCancelRoomBlock={onCancelRoomBlock}
          id={hotelData?.id}
        />
      )}
      {hotelData?.id && (
        <AccommodationsDeleteDialog
          id={hotelData.id}
          type={TravelMapBlockType.travelMapAccommodationsRoomblock}
          showCloseIcon
          pageSlug={pageSlug}
          isSuperAdmin={isSuperAdmin}
        />
      )}
    </ScrollableArea>
  );
};
